import React, { ErrorInfo, ReactNode } from "react";
import { ViewerUnavailable } from "./ViewerChrome";

/**
 * Contains a viewer that fails outside of its own error handling.
 *
 * Both document viewers are loaded as separate chunks (see FileDisplay), and a
 * chunk that does not arrive throws where nothing in the viewer can catch it: the
 * import rejects, React re-throws it out of the Suspense boundary, and with no
 * boundary above that React unmounts the whole tree — the entire page goes
 * blank, including the unauthenticated sharing-link route. That is not a remote
 * possibility: the app is a CRA build with content-hashed chunk names, so every
 * deploy invalidates the chunk of every session still open on the old build,
 * and clicking a .docx afterwards is what asks for it.
 *
 * A render that throws for any other reason lands here too, which is the point
 * of putting it around the viewer rather than only around the import.
 *
 * Reset by remounting: FileDisplay keys this on the document's url, so a
 * different document gets a fresh boundary rather than inheriting the last
 * one's failure.
 */
class ViewerErrorBoundary extends React.Component<{ children: ReactNode }, { failed: boolean }> {
    state = { failed: false };

    static getDerivedStateFromError() {
        return { failed: true };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error("Could not load document viewer", error, info.componentStack);
    }

    render() {
        return this.state.failed ? <ViewerUnavailable /> : this.props.children;
    }
}

export default ViewerErrorBoundary;
